<template lang="pug">
.future-online-meeting-list-tab
  .mb-3
  OnlineMeetingTable(v-model="items" :fields="fields" :totalRows.sync="rows" :currentPage.sync="currentPage" :isLoading="isLoading"
    :perPage="perPage" :filter="filter"
    @onDetail="onDetail" @onEdit="onEdit" @onDelete="onDelete" @onMeeting="onMeeting")
  .table-options.d-flex.flex-row.justify-content-end.align-items-center.flex-wrap
    // 検索
    .search-filter.d-flex.mr-1.align-items-center.mr-2
      b-input-group.search-filter-input-group.mr-1(size="md")
        b-input-group-prepend
          b-input-group-text.search-filter-icon
            .search-icon
        b-form-input.search-filter-input(v-model="filter.searchWord" type="search" placeholder="検索ワードを入力してください")
      HelpIcon.d-flex.justify-content-end.align-items-center(popoverId="future-search-filter-help" title="検索項目について")
        template(v-slot:text)
          div 検索項目は以下になります。
          div ・件名
    // フィルタリング
    span.table-option-text 会議相手:
    .filter
      b-form-select(id="future-filter-input" v-model="filter.id" :options="filterOptions" size="md")
    // ページあたりの行数
    span.table-option-text 1ページの行数:
    .per-page
      b-form-select(id="future-per-page-select" v-model="perPage" :options="pageOptions" size="md" @change="selectPerPage")
    // ページネーション
    b-pagination.m-0(v-model="currentPage" :total-rows="rows" :per-page="perPage" :disabled="isLoading" @change="selectPage" hide-goto-end-buttons)
    
  //- all page layer loading
  PageLoading(:isBusy="isBusy" :message="loadingMessage")
  //- delete event modal
  OkCancelModal(v-model="showDeleteModal" :title="deleteMessage" @ok="deleteMeeting(deleteTargetItem)")
  OkModal(v-model="showDeleteSuccessModal" title="削除に成功しました。")
</template>

<script lang="ts">
import { Component, Prop, Inject, Vue } from "vue-property-decorator";

import { userStore, itemListFilterStore } from "@/store/store";
import { tabIndexesStore } from "@/store/store";
import { PcUrl } from "@/commons/constants/PcUrl";
import Setting from "../../../../public/setting.json";
import { errorHandle } from "@/errorHandler";

import OtherOfficeOnlineMeetingsService, { OtherOfficeOnlineMeetingGetModel } from "@/commons/services/OtherOfficeOnlineMeetings";
import OtherOfficeConnectionsService, { OtherOfficeConnectionGetModel } from "@/commons/services/OtherOfficeConnections";

import PageLoading from "@/pc/basics/PageLoading.vue";
import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import HelpIcon from "@/pc/basics/HelpIcon.vue";

import OnlineMeetingTable, { FilterSetting, OnlineMeetingTableField, OnlineMeetingTableItem } from "@/pc/containers/otherOfficeOnlineMeetings/OnlineMeetingTable.vue";

/**
 * ### オンライン会議一覧 予定タブコンポーネント
 */
@Component({
  components: {
    PageLoading,
    OkCancelModal,
    OkModal,
    HelpIcon,
    OnlineMeetingTable
  }
})
export default class FutureOnlineMeetingListTab extends Vue {
  /** 待機者ラベルサービス */
  @Inject("OtherOfficeOnlineMeetingsService") meetingsService!: OtherOfficeOnlineMeetingsService;
  /** 他事業所連携管理サービス */
  @Inject("OtherOfficeConnectionsService") otherOfficeConnectionsService!: OtherOfficeConnectionsService;

  /** タブインデックス */
  @Prop()
  tabIndex!: number;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";

  /** テーブル列 */
  fields: Array<OnlineMeetingTableField> = [];
  /** テーブル行 */
  items: Array<OnlineMeetingTableItem> = [];
  /** 読込み中か否か */
  isLoading: boolean = true;
  /** ページングの現在ページ番号 */
  currentPage: number = 1;
  /** ページングの全体行数 */
  rows: number = 0;
  /** 1ページに表示する行数 */
  perPage: number = Number(Setting.otherOfficeOnlineMeetings.futureTable.perPage);
  /** 1ページに表示する行数の選択項目 */
  pageOptions: number[] = Setting.otherOfficeOnlineMeetings.futureTable.pageOptions;
  /** フィルタリング条件 */
  filter: FilterSetting = { searchWord: "", id: "" };
  /** フィルター選択項目 */
  filterOptions: Array<{ value: string; text: string }> = [];

  /** 削除確認モーダル表示可否 */
  showDeleteModal: boolean = false;
  /** 削除確認モーダルメッセージ */
  deleteMessage: string = "";
  /** 削除対象のイベント */
  deleteTargetItem: OnlineMeetingTableItem | null = null;
  /** 削除完了モーダル表示可否 */
  showDeleteSuccessModal: boolean = false;

  /** ### vue lifecycle mounted */
  public async mounted(): Promise<void> {
    const fields: Array<OnlineMeetingTableField> = this.createFields();
    this.fields = fields;

    await this.createView();
    return;
  }

  /**
   * ### テーブル列名を作成する
   */
  public createFields(): Array<OnlineMeetingTableField> {
    const fields: Array<OnlineMeetingTableField> = [];
    fields.push({ key: "startAndEnd", label: "日時", sortable: false, sortDirection: "desc", class: "start-and-end" });
    fields.push({ key: "isMain", label: "主催者", sortable: false, sortDirection: "desc", class: "is-main text-center" });
    fields.push({ key: "targetFacilityName", label: "会議相手", sortable: false, sortDirection: "desc", class: "target-facility-name" });
    fields.push({ key: "title", label: "件名", sortable: false, sortDirection: "desc", class: "title" });
    fields.push({ key: "futureOperation", label: "操作", sortable: false, sortDirection: "desc", class: "future-operation text-center" });
    return fields;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    this.isLoading = true;

    this.setRows();

    const otherOfficeConnections: Array<OtherOfficeConnectionGetModel> = await this.getOtherOfficeConnections();
    const filterOptions = this.createFilterOptions(otherOfficeConnections);
    this.filterOptions = filterOptions;
    // フィルタの復元
    this.restoreFilterSetting();

    const meetings: Array<OtherOfficeOnlineMeetingGetModel> = await this.getMeetings();
    this.items = meetings;

    this.isLoading = false;

    return;
  }

  /** ### ページング用の全件数を設定する */
  public async setRows() {
    try {
      // 検索条件が指定されている場合は、その検索条件を使用し、無ければデフォルトを使用する
      let params: any = {};
      // 過去の面会予約は表示対象外にするため、開始日時を指定する
      const start: number = this.$dateUtil.convertDateToNumber(new Date(), "yyyymmdd000000000");
      params = {
        start: start
      };
      const count: number = await this.meetingsService.getCount(userStore, params);
      this.rows = count;
      return;
    } catch (error) {
      errorHandle(error, this);
    }
  }

  /** ### 他事業所連携管理を取得する */
  public async getOtherOfficeConnections(): Promise<Array<OtherOfficeConnectionGetModel>> {
    const response: Array<OtherOfficeConnectionGetModel> = await this.otherOfficeConnectionsService.gets(userStore);
    return response;
  }

  /**
   * ### 会議アイ邸フィルター用選択項目
   *
   * - 検索用に利用者グループマスターのMAPを作る
   * - アカウント情報から許可されている利用者グループIDの配列を取得する
   * - 利用者グループマスターと照合して選択項目の利用者グループを作成する
   */
  public createFilterOptions(connections: Array<OtherOfficeConnectionGetModel>): Array<{ value: string; text: string }> {
    const filterOptions: Array<{ value: string; text: string }> = [];

    filterOptions.push({
      value: "",
      text: "全て"
    });
    for (const connection of connections) {
      if (connection.registerd === true && connection.registerdFacilityId != null) {
        filterOptions.push({ value: `${connection.registerdFacilityId}`, text: connection.officeName });
      }
    }

    return filterOptions;
  }

  /** フィルタ情報がstoreに存在する場合は復元する */
  private restoreFilterSetting(): void {
    if (this.$options.name && itemListFilterStore.filterMap[this.$options.name] != undefined) {
      this.filter = itemListFilterStore.filterMap[this.$options.name];
    }
    return;
  }

  /** ### オンライン会議を取得する */
  public async getMeetings(): Promise<Array<OtherOfficeOnlineMeetingGetModel>> {
    // 予定を表示するので、開始日時を指定する
    const start: number = this.$dateUtil.convertDateToNumber(new Date(), "yyyymmdd000000000");
    const params: any = {
      start: start
    };
    const response: Array<OtherOfficeOnlineMeetingGetModel> = await this.meetingsService.gets(userStore, params);
    return response;
  }

  /** ### 1ページあたりの表示行数変更イベント */
  public async selectPerPage(perPage: number): Promise<void> {
    this.perPage = perPage;
    this.currentPage = 1;
    return;
  }

  /** ### ページネーションのページ番号押下 */
  public async selectPage(page: number): Promise<void> {
    this.currentPage = page;
    return;
  }

  /**
   * ### 詳細ボタン押下イベント
   */
  public onDetail(item: OnlineMeetingTableItem): void {
    tabIndexesStore.setOtherOfficeOnlineMeetingsTabIndex({ index: this.tabIndex });
    this.$router.push({ name: PcUrl.OtherOfficeOnlineMeetingDetail, params: { targetFacilityId: `${item.targetFacilityId}`, id: `${item.id}` } });
    return;
  }

  /**
   * ### 修正ボタン押下イベント
   */
  public onEdit(item: OnlineMeetingTableItem): void {
    tabIndexesStore.setOtherOfficeOnlineMeetingsTabIndex({ index: this.tabIndex });
    this.$router.push({ name: PcUrl.OtherOfficeOnlineMeetingEdit, params: { targetFacilityId: `${item.targetFacilityId}`, id: `${item.id}` } });
    return;
  }

  /**
   * ### 削除ボタン押下イベント
   */
  public onDelete(item: OnlineMeetingTableItem): void {
    this.deleteTargetItem = item;
    this.deleteMessage = `${item.title}を削除します。よろしいですか？`;
    this.showDeleteModal = true;
    return;
  }

  /**
   * ### 削除の実施
   */
  public async deleteMeeting(deleteTargetItem: OnlineMeetingTableItem): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "削除中...";

    await this.meetingsService.delete(userStore, deleteTargetItem.targetFacilityId, deleteTargetItem.id);

    // 削除に成功したらリストから削除対象を除外する
    this.items = this.items.concat().filter((item) => {
      if (item.id == deleteTargetItem.id) {
        return false;
      } else {
        return true;
      }
    });

    this.isBusy = false;
    this.showDeleteSuccessModal = true;
    return;
  }

  /**
   * ### 会議開始ボタン押下イベント
   */
  public onMeeting(item: OnlineMeetingTableItem): void {
    tabIndexesStore.setOtherOfficeOnlineMeetingsTabIndex({ index: this.tabIndex });
    window.open(item.onlineMeetingUrl, "_blank");
    return;
  }

  setFilterSetting() {
    // 画面を離れる前に現在のフィルタリング条件をVuexに保存
    if (this.$options.name) {
      itemListFilterStore.setFilter({ url: this.$options.name, filter: this.filter });
    }
    return;
  }
}
</script>

<style lang="scss" scoped>
.future-online-meeting-list-tab {
  width: 100%;
  margin-left: auto;
  margin-right: auto;
}
</style>
